import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileBarChart, Ban, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { fmtH } from "../components/work/WorkLog";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const FLAG_STYLES = {
  CAPACITY_REVIEW_RECOMMENDED: "bg-[#FFEDD5] text-[#9A3412] border-[#FED7AA]",
  CONTEXT_SWITCHING: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  LOW_CAPACITY_DECLARED: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  ASSISTANCE_REQUESTED: "bg-[#E0E7FF] text-[#3730A3] border-[#C7D2FE]",
};
const PERIODS = [7, 14, 30];

function Stat({ value, label, testId, warn }) {
  return (
    <div className="text-right min-w-[64px]" data-testid={testId}>
      <div className={`text-lg font-heading font-semibold ${warn ? "text-[#9A3412]" : ""}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

function WorkStrip({ work, days, slug }) {
  const { t } = useLang();
  if (!work) return null;
  const cells = [
    { k: "open", v: work.open_items, l: t("openItems") },
    { k: "wip", v: work.in_progress, l: t("inProgressShort") },
    { k: "blocked", v: work.blocked, l: t("blockedShort"), warn: work.blocked > 0, icon: Ban },
    { k: "overdue", v: work.overdue, l: t("overdueShort"), warn: work.overdue > 0, icon: AlertTriangle },
    { k: "points", v: work.points, l: t("pointsOpen") },
    { k: "done", v: work.done_in_period, l: `${t("doneInPeriod")} · ${days}d` },
  ];
  const noWork = work.open_items === 0 && work.done_in_period === 0 && work.logged_hours === 0;
  return (
    <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-x-5 gap-y-2" data-testid={`work-strip-${slug}`}>
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground w-28">{t("workItemsLoad")}</span>
      {noWork ? <span className="text-xs text-muted-foreground" data-testid={`work-none-${slug}`}>{t("noNativeWork")}</span> : (
        <>
          {cells.map((c) => (
            <div key={c.k} className="flex items-center gap-1.5" data-testid={`work-${c.k}-${slug}`}>
              {c.icon && <c.icon className={`h-3 w-3 ${c.warn ? "text-[#9A3412]" : "text-muted-foreground"}`} />}
              <span className={`text-sm font-semibold ${c.warn ? "text-[#9A3412]" : ""}`}>{c.v}</span>
              <span className="text-[11px] text-muted-foreground">{c.l}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground ml-auto" data-testid={`work-hours-${slug}`}>
            <Clock className="h-3 w-3" />
            <b className="text-foreground">{fmtH(work.estimate_hours)}</b> {t("estimateShort")}{" · "}
            <b className={work.estimate_hours > 0 && work.logged_hours > work.estimate_hours ? "text-[#991B1B]" : "text-foreground"}>{fmtH(work.logged_hours)}</b> {t("loggedInPeriod").toLowerCase()} · {days}d{" · "}
            <b className="text-foreground">{fmtH(work.remaining_hours)}</b> {t("remainingShort")}
          </div>
        </>
      )}
    </div>
  );
}

export default function TeamWorkload() {
  const { t } = useLang();
  const [rows, setRows] = useState(null);
  const [days, setDays] = useState(7);
  useEffect(() => {
    setRows(null);
    api.get("/team-workload", { params: { days } }).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [days]);

  const flagLabel = (f) => ({
    CAPACITY_REVIEW_RECOMMENDED: t("capacityReview"),
    CONTEXT_SWITCHING: t("contextSwitchFlag"),
    LOW_CAPACITY_DECLARED: t("lowCapacity"),
    ASSISTANCE_REQUESTED: t("assistanceRequested"),
  }[f] || f);

  return (
    <div className="space-y-6" data-testid="team-workload-page">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[240px]">
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t("teamWorkload")}</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{t("workloadIntro")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="workload-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map((d) => <SelectItem key={d} value={String(d)} data-testid={`workload-period-${d}`}>{t("periodDays").replace("{n}", d)}</SelectItem>)}</SelectContent>
          </Select>
          <Link to="/reports?code=RPT-005" data-testid="workload-open-report" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border text-xs hover:bg-secondary transition-colors">
            <FileBarChart className="h-3.5 w-3.5" />{t("openReport")}
          </Link>
        </div>
      </div>
      {!rows ? <Skeleton className="h-60 w-full" /> : (
        <div className="space-y-3">
          {rows.length === 0 && <div className="apha-card p-6 text-sm text-muted-foreground" data-testid="workload-empty">{t("noData")}</div>}
          {rows.map((r) => {
            const slug = r.user.email.split("@")[0];
            return (
              <div key={r.user.id} className="apha-card p-4" data-testid={`workload-row-${slug}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="min-w-[160px]">
                    <div className="font-medium text-sm">{r.user.name}</div>
                    <div className="text-[11px] text-muted-foreground">{r.user.email}</div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 flex-1">
                    {r.allocations.map((a) => (
                      <span key={a.project_key} className="text-xs bg-secondary rounded px-1.5 py-0.5" title={a.project_name}>{a.project_key} {a.percent}%</span>
                    ))}
                  </div>
                  <Stat value={`${r.total_allocation}%`} label={t("totalAllocation")} warn={r.total_allocation > 100} testId={`alloc-${slug}`} />
                  <Stat value={r.active_wip} label={t("activeWip")} testId={`wip-${slug}`} />
                  <Stat value={r.workload_check ? `${r.workload_check.available_capacity_percent}%` : "—"} label={t("availableCapacity")} testId={`capacity-${slug}`} />
                </div>
                <WorkStrip work={r.work} days={days} slug={slug} />
                {r.flags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {r.flags.map((f) => <span key={f} className={`text-xs border rounded px-1.5 py-0.5 ${FLAG_STYLES[f] || ""}`}>{flagLabel(f)}</span>)}
                  </div>
                )}
                {r.assistance_requests.length > 0 && (
                  <div className="mt-2 text-xs text-muted-foreground">{t("assistanceRequested")}: {r.assistance_requests.map((a) => a.details).join("; ")}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
